//Трекеры для обнволения с сервером
const syncTimers = {};
const lastStates = {};

import { getUserBriefcase } from "@/Groups/Briefcase/private/api/getUserBriefcase";
import { addAssetToBriefcase } from "@/Groups/Briefcase/private/api/addAssetToBriefcase";
import { deleteAssetFromBriefcase } from "@/Groups/Briefcase/private/api/deleteAssetFromBriefcase";
import { changeAssetIndex } from "@/Groups/Briefcase/private/api/changeAssetIndex";

// Уникальный ключ актива-объекта из поиска/pending ({api_id|secid, assetType})
function assetKey(asset) {
  return asset.assetType === 'stock_ru' ? `stock_ru:${asset.secid}` : `coin:${asset.api_id}`;
}

// Ключ элемента портфеля в серверной форме {index, coin, stock_ru}
function itemKey(item) {
  return item.coin ? `coin:${item.coin.api_id}` : `stock_ru:${item.stock_ru.secid}`;
}

// Проверяет, относится ли элемент портфеля к тому же типу, что и asset/assetType
function sameType(item, assetType) {
  return assetType === 'stock_ru' ? !!item.stock_ru : !!item.coin;
}

// Параметры API-запроса для актива-объекта из поиска
function assetParams(asset) {
  return asset.assetType === 'stock_ru' ? { stockRuId: asset.secid } : { coinId: asset.api_id };
}

// Параметры API-запроса для элемента портфеля
function itemParams(item) {
  return item.coin ? { coinId: item.coin.api_id } : { stockRuId: item.stock_ru.secid };
}

const Briefcase = {
  namespaced: true,

  state: {
    assets: [],
    pendingAssets: [],
    isLoadingAssets: false,
    isCoinSearchOpened: false,
    isEditing: false,
    isReady: false,
    // Общая для списка портфеля и поиска вкладка ('coin'/'stock_ru') — хранится в сторе,
    // т.к. оба компонента пересоздаются при открытии/закрытии поиска (v-if в WidgetModule)
    // и теряют локальный ref при каждом переключении.
    activeAssetTab: 'coin',
    // Счётчик подтверждённых изменений портфеля — Скринер подписан на него и
    // перезагружает список при add/delete (см. ScannerPage.vue)
    assetsRevision: 0,
  },

  actions: {
    togglePendingAsset({ commit }, asset) {
      commit('TOGGLE_PENDING_ASSET', asset);
    },

    setActiveAssetTab({ commit }, tab) {
      commit('SET_ACTIVE_ASSET_TAB', tab);
    },

    async syncPendingAssets({ state, commit, dispatch }) {
      const assetsInBriefcase = state.assets.map(itemKey);
      const promises = [];

      for (const asset of state.pendingAssets) {
        const isInBriefcase = assetsInBriefcase.includes(assetKey(asset));
        if (!isInBriefcase) {
          promises.push(addAssetToBriefcase(assetParams(asset)));
          commit('TOGGLE_ASSET', asset);
        } else {
          promises.push(deleteAssetFromBriefcase(assetParams(asset)));
          commit('TOGGLE_ASSET', asset);
        }
      }
      dispatch('toggleSearchSection', false);
      try {
        await Promise.all(promises);
        // Инкрементим только если реально были изменения — иначе Скринер дёрнется впустую
        if (promises.length > 0) {
          commit('BUMP_ASSETS_REVISION');
        }
      } catch (err) {
        console.error('Error syncing pending assets:', err);
      }
      commit('CLEAR_PENDING_ASSETS');
    },

    toggleAsset({ commit, state }, asset) {
      const key = assetKey(asset);
      const wasInAssets = state.assets.some(el => itemKey(el) === key);
      //1.Мгновенно обновим UI
      commit('TOGGLE_ASSET', asset);
      //2.Сохраняем "последнее состояние" для сравнения позже
      lastStates[key] = !wasInAssets;
      //3.Если пользователь повторно кликает — сбросить предыдущий таймер
      if (syncTimers[key]) {
        clearTimeout(syncTimers[key]);
      }
      //4.Установить новый debounce таймер
      syncTimers[key] = setTimeout(async () => {
        const isNowInAssets = state.assets.some(el => itemKey(el) === key);
        //5. Если состояние изменилось по сравнению с тем, что было ДО клика — отправляем
        if (isNowInAssets !== wasInAssets) {
          try {
            (isNowInAssets)
              ? await addAssetToBriefcase(assetParams(asset))
              : await deleteAssetFromBriefcase(assetParams(asset));
            // Кэш скринера на бэке уже сброшен (on_commit) — сигналим Скринеру перезагрузиться
            commit('BUMP_ASSETS_REVISION');
          } catch (err) {
            console.error("Sync error:", err);
          }
        }
        delete syncTimers[key];
        delete lastStates[key];
      }, 1000);
    },

    toggleSearchSection({ commit }, status){
      commit('SET_SEARCH_SECTION_OPENED_FLAG', status);
    },

    async getAssets({ commit }){
      commit('SET_LOADING_FLAG', true);
      const data = await getUserBriefcase();
      commit('SET_READY_FLAG');
      commit('SET_ASSETS', data.assets);
      commit('SET_LOADING_FLAG', false);
    },

    // Переупорядочивание внутри одного типа актива (вкладки не смешиваются)
    async moveAsset({ commit, state }, { assetType, from, to }) {
      if (from === to) return;

      const typedItems = state.assets.filter(item => sameType(item, assetType));
      const otherItems = state.assets.filter(item => !sameType(item, assetType));

      const reordered = [...typedItems];
      const movedItem = reordered.splice(from, 1)[0];
      reordered.splice(to, 0, movedItem);

      const updatedIndexes = [];
      reordered.forEach((el, i) => {
        if (el.index !== i) {
          el.index = i;
          updatedIndexes.push({ ...itemParams(el), index: i });
        }
      });

      commit('SET_ASSETS', [...otherItems, ...reordered]);

      try {
        await Promise.all(updatedIndexes.map(({ coinId, stockRuId, index }) =>
          changeAssetIndex({ coinId, stockRuId, index })
        ));
      } catch (err) {
        console.error('Failed to sync moved indexes:', err);
      }
    },

    toggleEditing({ state, commit }){
      commit('SET_EDITING_FLAG', state.isEditing ? false : true);
    }
  },

  mutations: {
    SET_READY_FLAG(state){ state.isReady = true; },

    SET_EDITING_FLAG(state, status){ state.isEditing = status; },

    SET_ASSETS(state, data){ state.assets = data; },

    SET_LOADING_FLAG(state, status){ state.isLoadingAssets = status; },

    SET_SEARCH_SECTION_OPENED_FLAG(state, status){ state.isCoinSearchOpened = status; },

    SET_ACTIVE_ASSET_TAB(state, tab){ state.activeAssetTab = tab; },

    BUMP_ASSETS_REVISION(state){ state.assetsRevision += 1; },

    TOGGLE_PENDING_ASSET(state, asset) {
      const key = assetKey(asset);
      const index = state.pendingAssets.findIndex(a => assetKey(a) === key);
      if (index !== -1) {
        state.pendingAssets.splice(index, 1);
      } else {
        state.pendingAssets.push(asset);
      }
    },

    CLEAR_PENDING_ASSETS(state) {
      state.pendingAssets = [];
    },

    TOGGLE_ASSET(state, asset) {
      const key = assetKey(asset);
      const foundIndex = state.assets.findIndex(el => itemKey(el) === key);
      if (foundIndex !== -1) {
        state.assets.splice(foundIndex, 1);
        // Реиндексация только элементов того же типа — второй тип не задет
        let i = 0;
        state.assets.forEach(el => {
          if (sameType(el, asset.assetType)) {
            el.index = i++;
          }
        });
      } else {
        const typeCount = state.assets.filter(el => sameType(el, asset.assetType)).length;
        state.assets.push(
          asset.assetType === 'stock_ru'
            ? { index: typeCount, coin: null, stock_ru: asset }
            : { index: typeCount, coin: asset, stock_ru: null }
        );
      }
    }
  },

  getters: {
    sortedAssets(state) {
      return [...state.assets].sort((a, b) => a.index - b.index);
    },
    coinAssets(state) {
      return state.assets.filter(a => a.coin).sort((a, b) => a.index - b.index);
    },
    stockRuAssets(state) {
      return state.assets.filter(a => a.stock_ru).sort((a, b) => a.index - b.index);
    },
    assetsRevision(state) {
      return state.assetsRevision;
    }
  }

}

export default Briefcase;
